// =========================================================================
// SafeToast — point de passage UNIQUE des messages d'erreur (incident #314)
// =========================================================================
//
// PROBLÈME RÉSOLU
//   163 appels dans 82 fichiers font :
//       toast.error(err.response?.data?.detail || err.message)
//   Sur un 422, FastAPI renvoie `detail` sous forme de TABLEAU d'objets :
//       {"detail":[{"type":"string_too_long","loc":["body","siret"],"msg":"..."}]}
//   sonner passe cette valeur à React comme enfant → « Objects are not valid
//   as a React child » → l'exception survient PENDANT le rendu → l'arbre se
//   démonte → PAGE BLANCHE. C'est le mécanisme exact de l'incident #314.
//
// POURQUOI ICI ET PAS DANS LES 82 FICHIERS
//   Les 82 appelants n'ont aucun point de passage HTTP commun (11 axios,
//   71 fetch). Leur SEUL point commun est l'objet `toast` de sonner, qui est
//   un singleton de module : le patcher une fois au boot corrige les 163
//   appels sans en modifier aucun, et sans alias de bundler.
//
//   `toast.error` est `writable: true, configurable: true` (sonner 2.0.7) —
//   vérifié avant d'écrire ce module, pas supposé.
//
// CE QUE CE MODULE NE FAIT PAS
//   Il ne rend pas la validation backend laxiste : `max_length=14` sur le
//   SIRET reste correct. Il rend l'ERREUR AFFICHABLE. La saisie est nettoyée
//   en amont par `normalizeIdentifier` (lib/normalize.js).

import { isValidElement } from "react";
import { toast } from "sonner";

/** Libellé lisible d'un champ à partir du `loc` FastAPI : ["body","siret"] → "siret". */
function fieldFromLoc(loc) {
  if (!Array.isArray(loc)) return null;
  const parts = loc.filter((p) => p !== "body" && p !== "query" && p !== "path");
  return parts.length ? parts.join(".") : null;
}

/** Message lisible d'une entrée de validation FastAPI. */
function messageFromValidationItem(item) {
  if (typeof item === "string") return item;
  if (!item || typeof item !== "object") return null;
  const msg = item.msg || item.message || item.detail || null;
  const field = fieldFromLoc(item.loc);
  if (msg && field) return `${field} : ${msg}`;
  return msg || field || null;
}

/**
 * Transforme n'importe quelle valeur d'erreur d'API en chaîne affichable.
 *
 * Pure et sans dépendance — c'est la fonction couverte par les tests.
 *
 * @param {unknown} detail  la valeur brute (`err.response?.data?.detail`, …)
 * @param {string} [fallback] message si rien d'exploitable n'est trouvé
 * @returns {string}
 */
export function humanizeApiError(detail, fallback = "Une erreur est survenue") {
  // 1. Le cas nominal : déjà une chaîne.
  if (typeof detail === "string") return detail.trim() || fallback;

  // 2. Rien d'exploitable.
  if (detail == null) return fallback;

  // 3. Nombre / booléen : affichable tel quel.
  if (typeof detail === "number" || typeof detail === "boolean") return String(detail);

  // 4. Le cas #314 : tableau d'erreurs de validation FastAPI.
  if (Array.isArray(detail)) {
    const lignes = detail.map(messageFromValidationItem).filter(Boolean);
    if (!lignes.length) return fallback;
    // Au-delà de 3, on résume : un toast n'est pas un formulaire.
    if (lignes.length > 3) {
      return `${lignes.slice(0, 3).join(" · ")} (+${lignes.length - 3} autres)`;
    }
    return lignes.join(" · ");
  }

  // 5. Objet unique : soit une entrée de validation, soit une enveloppe.
  if (typeof detail === "object") {
    const direct = messageFromValidationItem(detail);
    if (direct) return direct;
    // Enveloppes courantes : {detail: ...}, {error: ...}
    if (detail.detail != null && detail.detail !== detail) {
      return humanizeApiError(detail.detail, fallback);
    }
    if (detail.error != null) return humanizeApiError(detail.error, fallback);
    return fallback;
  }

  return fallback;
}

/**
 * Vrai si la valeur peut être rendue telle quelle par React.
 * Les toasts JSX légitimes et les fonctions de rendu de sonner doivent
 * passer INTACTS — on ne corrige que ce qui casse.
 */
function estRendableTelQuel(value) {
  if (value == null) return false;
  if (typeof value === "string" || typeof value === "number") return true;
  if (isValidElement(value)) return true;      // <span>…</span>
  if (typeof value === "function") return true; // sonner accepte (id) => JSX
  return false;
}

/** Rend un argument de toast sûr, sans toucher à ce qui était déjà valide. */
export function sanitizeToastMessage(value) {
  if (estRendableTelQuel(value)) return value;
  return humanizeApiError(value);
}

// -------------------------------------------------------------------------
// Installation — idempotente, appelée une fois au boot (App.js)
// -------------------------------------------------------------------------

const METHODES_PATCHEES = ["error", "warning", "success", "info", "message", "loading"];
const MARQUEUR = "__omnihubSafeToastInstalled";

/**
 * Enveloppe les méthodes d'affichage de sonner pour qu'aucune valeur non
 * rendable ne parvienne à React.
 *
 * Idempotent : un second appel ne réempile pas les wrappers.
 * `toast.promise`, `toast.custom` et `toast.dismiss` ne sont PAS patchés —
 * leurs arguments ne sont pas des messages.
 *
 * @returns {boolean} true si l'installation a eu lieu, false si déjà installée
 */
export function installSafeToast(target = toast) {
  if (!target || target[MARQUEUR]) return false;

  for (const nom of METHODES_PATCHEES) {
    const original = target[nom];
    if (typeof original !== "function") continue;
    const wrapper = (message, ...reste) => original(sanitizeToastMessage(message), ...reste);
    // Préserve les propriétés éventuelles posées sur la méthode d'origine.
    Object.assign(wrapper, original);
    target[nom] = wrapper;
  }

  Object.defineProperty(target, MARQUEUR, { value: true, enumerable: false });
  return true;
}

export default installSafeToast;
